// src/pages/DoctorsJobsPage.js
import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { supabase } from '../supabase';
import { FaPaperPlane, FaTag, FaLock } from 'react-icons/fa';
import { IoBagHandleOutline, IoLocationOutline, IoStorefrontOutline } from "react-icons/io5";
import { IoBookmarkOutline, IoBookmark } from "react-icons/io5";
import { useAuth } from '../contexts/AuthContext';
import { fetchSponsoredEmployerIds } from '../services/listingPurchases';
import useSnackbarNotification from '../hooks/useSnackbarNotification';
import JobApplicationsTracker from '../components/home/JobApplicationsTracker';
import InterviewPracticeSection from '../components/home/InterviewPracticeSection';
import CVTracker from '../components/home/CVTracker';
import JobBoardSection from '../components/home/JobBoardSection';
import FAQSection from '../components/home/FAQSection';
import FeaturedJobs from '../components/employee/jobboard/FeaturedJobs';

const DoctorsJobsPage = () => {
  const { user } = useAuth();
  const { showSuccess, showError } = useSnackbarNotification();
  const location = useLocation();
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Handle hash navigation on page load
  useEffect(() => {
    const hash = window.location.hash;
    if (hash) {
      const sectionId = hash.substring(1); // Remove the # symbol
      setTimeout(() => {
        const element = document.getElementById(sectionId);
        if (element) {
          const navbarHeight = 80; // Approximate navbar height
          const elementPosition = element.getBoundingClientRect().top + window.pageYOffset;
          const offsetPosition = elementPosition - navbarHeight;
          window.scrollTo({
            top: offsetPosition,
            behavior: 'smooth'
          });
        }
      }, 100);
    }
  }, []);
  const [categoryFilter, setCategoryFilter] = useState('');
  const [locationFilter, setLocationFilter] = useState('');
  const [provinceFilter, setProvinceFilter] = useState('');
  const [jobTypeFilter, setJobTypeFilter] = useState('');
  const [tagFilter, setTagFilter] = useState('');
  const [filteredJobs, setFilteredJobs] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [sortOrder, setSortOrder] = useState('default');
  const [availableCategories, setAvailableCategories] = useState([]);
  const [availableLocations, setAvailableLocations] = useState([]);
  const [availableProvinces, setAvailableProvinces] = useState([]);
  const [availableJobTypes, setAvailableJobTypes] = useState([]);
  const [featuredJobs, setFeaturedJobs] = useState([]);
  const [rawFeaturedJobs, setRawFeaturedJobs] = useState([]);
  const [bookmarkedInternalJobs, setBookmarkedInternalJobs] = useState([]);
  const [shareDropdown, setShareDropdown] = useState({ open: false, job: null });
  const [dismissedJobMatches, setDismissedJobMatches] = useState([]);

  // Helper function to capitalize first letter of words
  const capitalizeFirstLetter = (str) => {
    if (!str) return str;
    // Handle hyphenated words like "full-time" -> "Full-time"
    return str.split('-').map(word =>
      word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
    ).join('-');
  };
  const [availableTags, setAvailableTags] = useState([]);
  const [displayedJobs, setDisplayedJobs] = useState([]);
  const [bookmarkedJobs, setBookmarkedJobs] = useState([]);

  // Handle hash navigation on page load and hash changes
  useEffect(() => {
    const scrollToSection = (hash) => {
      if (!hash) return;

      const sectionId = hash.substring(1); // Remove the # symbol

      // Function to attempt scrolling with retries
      const attemptScroll = (retries = 10) => {
        const element = document.getElementById(sectionId);
        if (element) {
          const navbarHeight = 80; // Approximate navbar height
          const elementPosition = element.getBoundingClientRect().top + window.pageYOffset;
          const offsetPosition = elementPosition - navbarHeight;
          window.scrollTo({
            top: offsetPosition,
            behavior: 'smooth'
          });
          return true;
        } else if (retries > 0) {
          // Retry after a short delay if element not found yet
          setTimeout(() => attemptScroll(retries - 1), 100);
        }
        return false;
      };

      // Initial attempt with delay to allow content to render
      setTimeout(() => {
        attemptScroll();
      }, 300);
    };

    // Handle hash on initial load
    if (window.location.hash) {
      scrollToSection(window.location.hash);
    }

    // Listen for hash changes
    const handleHashChange = () => {
      scrollToSection(window.location.hash);
    };

    window.addEventListener('hashchange', handleHashChange);

    return () => {
      window.removeEventListener('hashchange', handleHashChange);
    };
  }, [location.pathname, location.hash]);

  // Fetch jobs from Supabase
  useEffect(() => {
    const fetchJobs = async () => {
      try {
        setLoading(true);

        // Get all jobs from Supabase
        const { data, error: supabaseError } = await supabase
          .from('jobs')
          .select('*')
          .eq('live', true)
          .limit(2000);

        if (supabaseError) throw supabaseError;
        console.log(data);
        // Sort the jobs by id or date added if available
        const sortedData = data ? [...data].sort((a, b) => {
          // First try to sort by date_added if available
          if (a.date_added && b.date_added) {
            return new Date(b.date_added) - new Date(a.date_added); // newest first
          }
          // Fall back to id sorting
          return a.id - b.id;
        }) : [];
        setJobs(sortedData);

        // If user is not logged in, only the first 6 jobs will be displayed
        if (!user) {
          setFilteredJobs(sortedData);
        } else {
          setFilteredJobs(sortedData);
        }

        // Extract unique categories, locations, provinces, job types, and tags
        if (sortedData && sortedData.length > 0) {
          // Only consider jobs whose deadlines have not passed when building
          // the category pills and other filter options, so we don't show
          // categories with zero live listings.
          const validJobsForFilters = sortedData.filter(job => isDeadlineValid(job.deadline));

          const categories = [...new Set(validJobsForFilters.map(job => job.category))].filter(Boolean);
          const locations = [...new Set(validJobsForFilters.map(job => job.continent))].filter(Boolean);
          const provinces = [...new Set(validJobsForFilters.map(job => job.province))].filter(Boolean);
          const jobTypes = [...new Set(validJobsForFilters.map(job => job.fulltime_parttime))].filter(Boolean);

          // Extract tags - if tags are comma-separated in a single field
          const allTags = [];
          validJobsForFilters.forEach(job => {
            if (job.tags) {
              const jobTags = job.tags.split(',').map(tag => tag.trim());
              allTags.push(...jobTags);
            }
          });
          const uniqueTags = [...new Set(allTags)].filter(Boolean);

          setAvailableCategories(categories.sort());
          setAvailableLocations(locations.sort());
          setAvailableProvinces(provinces.sort());
          setAvailableJobTypes(jobTypes.sort());
          setAvailableTags(uniqueTags.sort());
        }
      } catch (err) {
        console.error('Error fetching jobs:', err);
        setError('Failed to load jobs. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    fetchJobs();
  }, [user]);

  // Helper function to check if deadline has passed
  const isDeadlineValid = (deadlineString) => {
    if (!deadlineString) return true; // If no deadline, show the job

    // If deadline is "see website" or similar non-date text, show the job
    if (deadlineString.toLowerCase().includes('see website') ||
      deadlineString.toLowerCase().includes('website') ||
      deadlineString.toLowerCase().includes('apply') ||
      !deadlineString.match(/\d/)) {
      return true;
    }

    try {
      // Parse date format like "10 Jun 25" or "22 May 25"
      const dateMatch = deadlineString.match(/(\d{1,2})\s+([A-Za-z]{3})\s+(\d{2})/);
      if (!dateMatch) return true; // If we can't parse it, show the job

      const [, day, monthStr, year] = dateMatch;
      const monthNames = ['jan', 'feb', 'mar', 'apr', 'may', 'jun',
        'jul', 'aug', 'sep', 'oct', 'nov', 'dec'];
      const monthIndex = monthNames.indexOf(monthStr.toLowerCase());

      if (monthIndex === -1) return true; // If invalid month, show the job

      // Assume years 00-30 are 2000-2030, 31-99 are 1931-1999
      const fullYear = parseInt(year) <= 30 ? 2000 + parseInt(year) : 1900 + parseInt(year);

      const deadlineDate = new Date(fullYear, monthIndex, parseInt(day));
      const today = new Date();
      today.setHours(0, 0, 0, 0); // Reset time to start of day for fair comparison

      return deadlineDate >= today;
    } catch (error) {
      console.warn('Error parsing deadline:', deadlineString, error);
      return true; // If there's an error parsing, show the job
    }
  };

  // Filter and sort jobs based on all filters
  useEffect(() => {
    let result = jobs;

    // First, filter out jobs with passed deadlines
    result = result.filter(job => isDeadlineValid(job.deadline));

    // If user is not logged in, we still filter the jobs to show accurate total count
    // but we'll limit display to 6 in another useEffect
    if (!user) {
      setFilteredJobs(result);
      return;
    }

    // Apply category filter
    if (categoryFilter) {
      result = result.filter(job => job.category === categoryFilter);
    }

    // Apply location filter
    if (locationFilter) {
      result = result.filter(job => job.continent === locationFilter);
    }

    // Apply province filter
    if (provinceFilter) {
      result = result.filter(job => job.province === provinceFilter);
    }

    // Apply job type filter
    if (jobTypeFilter) {
      result = result.filter(job => job.fulltime_parttime === jobTypeFilter);
    }

    // Apply tag filter
    if (tagFilter && tagFilter !== '') {
      result = result.filter(job =>
        job.tags && job.tags.toLowerCase().includes(tagFilter.toLowerCase())
      );
    }

    // Apply search query
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      result = result.filter(job =>
        (job.title && job.title.toLowerCase().includes(query)) ||
        (job.company && job.company.toLowerCase().includes(query)) ||
        (job.location && job.location.toLowerCase().includes(query)) ||
        (job.tags && job.tags.toLowerCase().includes(query))
      );
    }

    // Apply sorting
    if (sortOrder !== 'default') {
      result = [...result].sort((a, b) => {
        if (sortOrder === 'linkedin-yes') {
          // Show LinkedIn jobs first
          if (a.linkedin && !b.linkedin) return -1;
          if (!a.linkedin && b.linkedin) return 1;
          // If both have same LinkedIn status, sort by date
          const dateA = a.date_added ? new Date(a.date_added) : null;
          const dateB = b.date_added ? new Date(b.date_added) : null;
          if (!dateA && !dateB) return 0;
          if (!dateA) return 1;
          if (!dateB) return -1;
          return dateB - dateA;
        } else if (sortOrder === 'linkedin-no') {
          // Show non-LinkedIn jobs first
          if (!a.linkedin && b.linkedin) return -1;
          if (a.linkedin && !b.linkedin) return 1;
          // If both have same LinkedIn status, sort by date
          const dateA = a.date_added ? new Date(a.date_added) : null;
          const dateB = b.date_added ? new Date(b.date_added) : null;
          if (!dateA && !dateB) return 0;
          if (!dateA) return 1;
          if (!dateB) return -1;
          return dateB - dateA;
        } else {
          // Handle date-based sorting
          const dateA = a.date_added ? new Date(a.date_added) : null;
          const dateB = b.date_added ? new Date(b.date_added) : null;

          // Handle cases where one or both dates are missing
          if (!dateA && !dateB) return 0;
          if (!dateA) return 1;
          if (!dateB) return -1;

          // Sort based on the selected order
          if (sortOrder === 'newest') {
            return dateB - dateA; // Newest first
          } else if (sortOrder === 'oldest') {
            return dateA - dateB; // Oldest first
          }
        }
        return 0;
      });
    }

    setFilteredJobs(result);
  }, [categoryFilter, locationFilter, provinceFilter, jobTypeFilter, tagFilter, searchQuery, sortOrder, jobs, user]);

  // Prepare the displayed jobs
  useEffect(() => {
    if (!user) {
      // For non-logged in users, limit to 6 jobs (5 fully visible + 1 blurred)
      setDisplayedJobs(jobs.slice(0, 5));
    } else {
      // For logged in users, show all filtered jobs
      setDisplayedJobs(filteredJobs);
    }
  }, [filteredJobs, jobs, user]);

  useEffect(() => {
    const fetchBookmarkedJobs = async () => {
      if (!user) return;

      try {
        const { data, error } = await supabase
          .from('my_applications')
          .select('job_id')
          .eq('user_id', user.id);

        if (error) throw error;

        setBookmarkedJobs(data.map(item => item.job_id));
      } catch (error) {
        console.error('Error fetching bookmarked jobs:', error);
      }
    };

    fetchBookmarkedJobs();
  }, [user]);

  // Fetch dismissed job matches from user metadata
  useEffect(() => {
    const fetchDismissedJobMatches = async () => {
      if (!user) return;

      try {
        const dismissed = user.user_metadata?.dismissed_job_matches || [];
        setDismissedJobMatches(dismissed);
      } catch (error) {
        console.error('Error fetching dismissed job matches:', error);
      }
    };

    fetchDismissedJobMatches();
  }, [user]);

  // Fetch bookmarked internal jobs
  useEffect(() => {
    const fetchBookmarkedInternalJobs = async () => {
      if (!user) return;

      try {
        const { data, error } = await supabase
          .from('applications')
          .select('job_listing_id')
          .eq('user_id', user.id)
          .eq('status', 'bookmarked');

        if (error) throw error;

        setBookmarkedInternalJobs(data.map(item => item.job_listing_id));
      } catch (error) {
        console.error('Error fetching bookmarked internal jobs:', error);
      }
    };

    fetchBookmarkedInternalJobs();
  }, [user]);

  // Fetch featured jobs (job matches)
  useEffect(() => {
    const fetchFeaturedJobs = async () => {
      if (!user) return;

      try {
        // Fetch employee languages from database, with fallback to user_metadata
        let employeeLanguages = null;
        try {
          const { data: profileData } = await supabase
            .from('users_profiles')
            .select('languages')
            .eq('profileid', user.id)
            .single();

          if (profileData?.languages) {
            // Parse JSON string if needed
            if (typeof profileData.languages === 'string') {
              try {
                employeeLanguages = JSON.parse(profileData.languages);
              } catch {
                employeeLanguages = null;
              }
            } else {
              employeeLanguages = profileData.languages;
            }
          }
        } catch (err) {
          // Database fetch failed, will try user_metadata below
        }

        // Fallback to user_metadata if database didn't have languages
        if (!employeeLanguages) {
          employeeLanguages = user.user_metadata?.my_profile?.profile_skills?.languages || null;
        }

        // Get employee profile data for matching
        const employeeProfile = {
          country: user.user_metadata?.country || user.user_metadata?.my_profile?.profile_personal_info?.country || null,
          provinceState: user.user_metadata?.province_state || user.user_metadata?.my_profile?.profile_personal_info?.provinceState || null,
          employmentType: user.user_metadata?.employment_status || null,
          trainingLevel: user.user_metadata?.training_level || null,
          // Use profile_role.categories (What category best fits what you're looking for? Select all that apply)
          category: user.user_metadata?.my_profile?.profile_role?.categories ||
            user.user_metadata?.profile_categories ||
            null,
          languages: employeeLanguages
        };

        const now = new Date();
        const thirtyDaysAgo = new Date(now);
        thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);

        // Fetch internal job listings
        const { data: jobListings, error } = await supabase
          .from('internal_job_listings')
          .select('*')
          .eq('status', 'published')
          .order('created_at', { ascending: false });

        if (error) throw error;

        let sponsoredEmployerIds = new Set();
        try {
          const sponsorIds = await fetchSponsoredEmployerIds();
          sponsoredEmployerIds = new Set(sponsorIds);
        } catch (err) {
          // Error fetching sponsored employers
        }

        // Get jobs from last 30 days
        let freshJobListings = (jobListings || []).filter(job => {
          if (!job?.created_at) return false;
          const createdAt = new Date(job.created_at);
          return createdAt >= thirtyDaysAgo;
        });

        // If no fresh jobs, use all available jobs (fallback)
        if (freshJobListings.length === 0) {
          freshJobListings = (jobListings || []).filter(job => {
            if (!job?.created_at) return false;
            const createdAt = new Date(job.created_at);
            return createdAt >= thirtyDaysAgo;
          });
        }

        // If still no jobs, use all available jobs (fallback)
        if (freshJobListings.length === 0) {
          freshJobListings = [...jobListings, ...filteredJobs] || [];
        }
        console.log('freshJobListings', freshJobListings);
        console.log('employeeProfile', employeeProfile);
        // Calculate matches
        const matches = freshJobListings.map(job => {
          let score = 0;
          let totalChecks = 0;

          // 1. Location match (25% weight)
          let locationScore = 0;
          if (employeeProfile.country && job.country) {
            const employeeCountryLower = employeeProfile.country.toLowerCase().trim();
            const jobCountryLower = job.country.toLowerCase().trim();
            const countryMatch = employeeCountryLower === jobCountryLower ||
              jobCountryLower.includes(employeeCountryLower) ||
              employeeCountryLower.includes(jobCountryLower);

            let provinceScore = 0;
            if (employeeProfile.provinceState && job.province) {
              const employeeProvinceLower = employeeProfile.provinceState.toLowerCase().trim();
              const jobProvinceLower = job.province.toLowerCase().trim();
              const provinceMatch = employeeProvinceLower === jobProvinceLower ||
                jobProvinceLower.includes(employeeProvinceLower) ||
                employeeProvinceLower.includes(jobProvinceLower);
              if (provinceMatch) {
                provinceScore = 12.5;
              }
            } else if (!employeeProfile.provinceState && !job.province) {
              provinceScore = 12.5;
            }

            if (countryMatch) {
              locationScore = 12.5;
              if (provinceScore > 0) {
                locationScore = 25;
              }
            }
          } else if (job.location && employeeProfile.country) {
            const employeeCountryLower = employeeProfile.country.toLowerCase().trim();
            const jobLocationLower = job.location.toLowerCase().trim();
            const locationMatch = jobLocationLower.includes(employeeCountryLower) ||
              employeeCountryLower.includes(jobLocationLower);
            locationScore = locationMatch ? 25 : 0;
          }

          if (employeeProfile.country && (job.country || job.location)) {
            totalChecks += 25;
            if (locationScore > 0) {
              score += locationScore;
            }
          }

          // 1b province match (25% weight) for scraped jobs
          let provinceScore = 0;
          if (employeeProfile.provinceState && job.province && !job.country) {
            const employeeProvinceLower = employeeProfile.provinceState.toLowerCase().trim();
            const jobProvinceLower = job.province.toLowerCase().trim();
            const provinceMatch = employeeProvinceLower === jobProvinceLower ||
              jobProvinceLower.includes(employeeProvinceLower) ||
              employeeProvinceLower.includes(jobProvinceLower);
            if (provinceMatch) {
              provinceScore = 12.5;
              if (locationScore > 0) {
                provinceScore = 25;
              }
            }
          }


          // 2. Employment type match (25% weight)
          let employmentTypeScore = 0;
          if (job.employment_type && employeeProfile.employmentType) {
            totalChecks += 25;
            const jobType = job.employment_type.toLowerCase();
            const employeeType = employeeProfile.employmentType.toLowerCase();
            const employmentMatch = jobType === employeeType ||
              (jobType.includes('full') && employeeType.includes('full')) ||
              (jobType.includes('part') && employeeType.includes('part'));

            if (employmentMatch) {
              score += 25;
              employmentTypeScore = 25;
            }
          }

          //2b employment type match (25% weight) for scraped jobs
          if (job.fulltime_parttime && employeeProfile.employmentType && !job.country) {
            totalChecks += 25;
            const jobType = job.fulltime_parttime.toLowerCase();
            const employmentMatch = jobType.includes(employeeProfile.employmentType.toLowerCase());
            if (employmentMatch) {
              score += 25;
              employmentTypeScore = 25;
            }
          }

          // 3. Experience level match (25% weight)
          let experienceScore = 0;
          const mapTrainingToExperience = (trainingLevel) => {
            if (!trainingLevel) return [];
            const level = trainingLevel.toLowerCase();
            switch (level) {
              case 'student':
                return ['medical-student'];
              case 'junior':
                return ['junior-doctor', 'medical-officer'];
              case 'senior':
                return ['registrar', 'fellow', 'specialist', 'consultant'];
              case 'other':
                return ['medical-student', 'junior-doctor', 'medical-officer', 'registrar', 'fellow', 'specialist', 'consultant'];
              default:
                return [];
            }
          };

          if (job.experience_level && employeeProfile.trainingLevel) {
            totalChecks += 25;
            const jobExperienceLevel = job.experience_level.toLowerCase();
            const compatibleLevels = mapTrainingToExperience(employeeProfile.trainingLevel);
            const experienceMatch = compatibleLevels.includes(jobExperienceLevel);

            if (experienceMatch) {
              score += 25;
              experienceScore = 25;
            }
          }

          //3b experience level match (25% weight) for scraped jobs
          if (job.experience && employeeProfile.trainingLevel && !job.country) {
            totalChecks += 25;
            const jobExperienceLevel = job.experience.toLowerCase();
            const experienceMatch = jobExperienceLevel.includes(employeeProfile.trainingLevel.toLowerCase());
            if (experienceMatch) {
              score += 25;
              experienceScore = 25; 
            }
          }

          // 4. Category/Roles match (25% weight) - "What category best fits what you're looking for?" vs Job specialty
          let categoryScore = 0;
          if (job.specialty && employeeProfile.category) {
            totalChecks += 25;
            const jobCategory = job.specialty.toLowerCase();
            // employeeProfile.category is an array from profile_role.categories
            if (Array.isArray(employeeProfile.category)) {
              // Check if any of the employee's selected categories matches the job specialty
              const matchingCategory = employeeProfile.category.find(cat => {
                if (!cat) return false;
                const catStr = typeof cat === 'string' ? cat.toLowerCase() : String(cat).toLowerCase();
                return catStr === jobCategory ||
                  catStr.includes(jobCategory) ||
                  jobCategory.includes(catStr);
              });
              const categoryMatch = !!matchingCategory;

              if (categoryMatch) {
                score += 25;
                categoryScore = 25;
              }
            } else if (typeof employeeProfile.category === 'string') {
              // Fallback for string format
              const categoryMatch = employeeProfile.category.toLowerCase() === jobCategory ||
                employeeProfile.category.toLowerCase().includes(jobCategory) ||
                jobCategory.includes(employeeProfile.category.toLowerCase());

              if (categoryMatch) {
                score += 25;
                categoryScore = 25;
              }
            }
          }

          //4b category/roles match (25% weight) for scraped jobs
          if (job.category && employeeProfile.category && !job.country) {
            totalChecks += 25;
            const jobCategory = job.category.toLowerCase();
            const categoryMatch = employeeProfile.category.includes(jobCategory);
            if (categoryMatch) {
              score += 25;
              categoryScore = 25;
            }
          }

          // 5. Languages match (25% weight) - Job language_requirement vs Employee languages
          let languageScore = 0;
          if (job.language_requirement && employeeProfile.languages) {
            totalChecks += 25;

            // Parse job language_requirement if it's a JSON string
            let jobLanguages = job.language_requirement;
            if (typeof jobLanguages === 'string') {
              try {
                jobLanguages = JSON.parse(jobLanguages);
              } catch {
                // If parsing fails, treat as single language string
                jobLanguages = [jobLanguages];
              }
            }
            // Ensure jobLanguages is an array
            if (!Array.isArray(jobLanguages)) {
              jobLanguages = [jobLanguages];
            }

            // Ensure employeeLanguages is an array
            let employeeLangsArray = employeeProfile.languages;
            if (!Array.isArray(employeeLangsArray)) {
              employeeLangsArray = [employeeLangsArray];
            }

            // Normalize languages to lowercase strings for comparison
            const normalizeLanguage = (lang) => {
              if (!lang) return '';
              const langStr = typeof lang === 'string' ? lang : String(lang);
              return langStr.toLowerCase().trim();
            };

            const jobLangsNormalized = jobLanguages.map(normalizeLanguage).filter(l => l.length > 0);
            const employeeLangsNormalized = employeeLangsArray.map(normalizeLanguage).filter(l => l.length > 0);

            // Check if all required job languages are covered by employee languages
            // Or if any job language matches any employee language (more lenient)
            let languageMatch = false;
            if (jobLangsNormalized.length > 0 && employeeLangsNormalized.length > 0) {
              // Check if all job required languages are present in employee languages
              const allRequiredMatch = jobLangsNormalized.every(jobLang =>
                employeeLangsNormalized.some(empLang =>
                  empLang === jobLang ||
                  empLang.includes(jobLang) ||
                  jobLang.includes(empLang)
                )
              );

              // Also check if any job language matches any employee language (more lenient approach)
              const anyMatch = jobLangsNormalized.some(jobLang =>
                employeeLangsNormalized.some(empLang =>
                  empLang === jobLang ||
                  empLang.includes(jobLang) ||
                  jobLang.includes(empLang)
                )
              );

              // Use the more lenient match (at least one language matches)
              languageMatch = anyMatch;

              // If all required languages match, that's a full match
              // For partial matches, we could give partial score, but for simplicity, 
              // we'll give full 25% if any language matches
              if (allRequiredMatch) {
                languageMatch = true;
              }
            }

            if (languageMatch) {
              score += 25;
              languageScore = 25;
            }
          }

          // Calculate match percentage accurately
          // If totalChecks is 0 (no profile data), assign a default match percentage so jobs still show
          let matchPercentage = 0;
          if (totalChecks > 0) {
            // Calculate percentage: (score / totalChecks) * 100
            // Ensure we get accurate percentage, not rounded prematurely
            const rawPercentage = (score / totalChecks) * 100;
            matchPercentage = Math.round(rawPercentage);
            // Ensure percentage is between 0 and 100
            matchPercentage = Math.max(0, Math.min(100, matchPercentage));
          } else {
            // If user has no profile data, assign a baseline match percentage
            // This ensures jobs still show up even without profile matching
            matchPercentage = 10; // Baseline 10% match
          }

          const postedDays = Math.max(
            1,
            Math.ceil((new Date() - new Date(job.created_at)) / (1000 * 60 * 60 * 24))
          );
          const isSponsored = sponsoredEmployerIds.has(job.employer_id);

          return {
            id: job.id,
            matchPercentage,
            title: job.title,
            company: job.company_name,
            location: job.location,
            postedDays,
            status: "available",
            created_at: job.created_at,
            isSponsored,
            employer_id: job.employer_id
          };
        })
          .sort((a, b) => {
            if (a.isSponsored !== b.isSponsored) {
              return a.isSponsored ? -1 : 1;
            }
            if (b.matchPercentage !== a.matchPercentage) {
              return b.matchPercentage - a.matchPercentage;
            }
            return new Date(b.created_at) - new Date(a.created_at);
          });

        // Filter out dismissed job matches
        const nonDismissedMatches = matches.filter(match =>
          !dismissedJobMatches.includes(match.id)
        );

        // Store raw featured jobs with original job data for filtering
        const featuredJobsWithData = nonDismissedMatches.map(match => {
          const originalJob = freshJobListings.find(j => j.id === match.id);
          return {
            ...match,
            originalJob: originalJob // Store original job data for filtering
          };
        });

        setRawFeaturedJobs(featuredJobsWithData);
      } catch (error) {
        console.error('Error fetching featured jobs:', error);
      }
    };

    fetchFeaturedJobs();
  }, [user, dismissedJobMatches, filteredJobs]);

  // Filter featured jobs based on active filters
  useEffect(() => {
    if (!user || rawFeaturedJobs.length === 0) {
      setFeaturedJobs([]);
      return;
    }

    let filtered = [...rawFeaturedJobs];

    // Apply category filter (specialty in internal_job_listings)
    if (categoryFilter) {
      filtered = filtered.filter(job => {
        if (!job.originalJob?.specialty) return false;
        // Normalize both for comparison
        const jobSpecialty = job.originalJob.specialty.toLowerCase().trim();
        const filterCategory = categoryFilter.toLowerCase().trim();
        return jobSpecialty === filterCategory ||
          jobSpecialty.includes(filterCategory) ||
          filterCategory.includes(jobSpecialty);
      });
    }

    // Apply location filter (country in internal_job_listings)
    if (locationFilter) {
      filtered = filtered.filter(job => {
        const jobCountry = job.originalJob?.country || '';
        const jobLocation = job.originalJob?.location || '';
        const filterLocation = locationFilter.toLowerCase().trim();

        if (jobCountry) {
          return jobCountry.toLowerCase().trim() === filterLocation ||
            jobCountry.toLowerCase().trim().includes(filterLocation) ||
            filterLocation.includes(jobCountry.toLowerCase().trim());
        }
        // Fallback to location field
        if (jobLocation) {
          return jobLocation.toLowerCase().trim().includes(filterLocation) ||
            filterLocation.includes(jobLocation.toLowerCase().trim());
        }
        return false;
      });
    }

    // Apply province filter
    if (provinceFilter) {
      filtered = filtered.filter(job => {
        const jobProvince = job.originalJob?.province || '';
        if (!jobProvince) return false;
        const filterProvince = provinceFilter.toLowerCase().trim();
        return jobProvince.toLowerCase().trim() === filterProvince ||
          jobProvince.toLowerCase().trim().includes(filterProvince) ||
          filterProvince.includes(jobProvince.toLowerCase().trim());
      });
    }

    // Apply job type filter (employment_type in internal_job_listings)
    if (jobTypeFilter) {
      filtered = filtered.filter(job => {
        const jobEmploymentType = job.originalJob?.employment_type || '';
        if (!jobEmploymentType) return false;
        const filterJobType = jobTypeFilter.toLowerCase().trim();
        const jobTypeLower = jobEmploymentType.toLowerCase().trim();
        return jobTypeLower === filterJobType ||
          (jobTypeLower.includes('full') && filterJobType.includes('full')) ||
          (jobTypeLower.includes('part') && filterJobType.includes('part'));
      });
    }

    // Apply search query
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(job => {
        const title = job.title?.toLowerCase() || '';
        const company = job.company?.toLowerCase() || '';
        const location = job.location?.toLowerCase() || '';
        return title.includes(query) ||
          company.includes(query) ||
          location.includes(query);
      });
    }

    // Get top matches (more than 3 for carousel)
    const topMatches = filtered.slice(0, 9);
    setFeaturedJobs(topMatches);
  }, [categoryFilter, locationFilter, provinceFilter, jobTypeFilter, searchQuery, rawFeaturedJobs, user]);

  const handleCategoryClick = (category) => {
    if (!user) {
      // If user is not logged in, show a message that they need to log in
      return;
    }

    if (categoryFilter === category) {
      // If already selected, clear the filter
      setCategoryFilter('');
    } else {
      // Set new filter
      setCategoryFilter(category);
    }
  };

  const clearAllFilters = () => {
    if (!user) return;

    setCategoryFilter('');
    setLocationFilter('');
    setProvinceFilter('');
    setJobTypeFilter('');
    setTagFilter('');
    setSearchQuery('');
    setSortOrder('default');
  };

  const handleSearch = (e) => {
    e.preventDefault();
    // Only process search if logged in
    if (!user) return;
    // The filtering will be handled by the useEffect above
  };

  // Find the most recent date_added to display
  const findMostRecentUpdate = (jobs) => {
    if (!jobs || jobs.length === 0) return null;

    let mostRecent = null;

    jobs.forEach(job => {
      if (job.date_added) {
        const jobDate = new Date(job.date_added);
        if (!mostRecent || jobDate > mostRecent) {
          mostRecent = jobDate;
        }
      }
    });

    return mostRecent ? formatDate(mostRecent.toISOString()) : null;
  };

  // Helper function to get category tag class
  const getCategoryClass = (category) => {
    if (!category) return '';

    // Normalize the category name for consistent CSS class handling
    // Remove spaces, slashes, convert to lowercase
    let normalizedCategory = category.toLowerCase()
      .replace(/\s+/g, '-')
      .replace(/\//g, '-')
      .replace(/[^a-z0-9-]/g, '');

    // Handle special cases based on your data
    if (normalizedCategory.includes('clinical-medicine')) return 'clinical-medicine';
    if (normalizedCategory.includes('medical-education')) return 'medical-education';
    if (normalizedCategory.includes('medical-communication')) return 'medical-communication';
    if (normalizedCategory.includes('project-management')) return 'project-management';
    if (normalizedCategory.includes('pharmaceuticals') || normalizedCategory.includes('pharmaceutical')) return 'pharmaceuticals';
    if (normalizedCategory.includes('clinical-research') || normalizedCategory.includes('clinical-reserach')) return 'clinical-research';
    if (normalizedCategory.includes('healthtech') || normalizedCategory.includes('health-tech')) return 'health-tech';
    if (normalizedCategory.includes('hospital') || normalizedCategory.includes('clinic-management')) return 'hospital-clinic-management';
    if (normalizedCategory.includes('consulting') || normalizedCategory === 'management') return 'management-consulting';
    if (normalizedCategory.includes('entrepreneurship')) return 'entrepreneurship';
    if (normalizedCategory.includes('policy') || normalizedCategory.includes('legal') || normalizedCategory.includes('ethics')) return 'policy';
    if (normalizedCategory.includes('marketing') || normalizedCategory.includes('sales')) return 'marketing';
    if (normalizedCategory.includes('creative')) return 'creative';
    if (normalizedCategory.includes('wellness') || normalizedCategory.includes('lifestyle')) return 'wellness';
    if (normalizedCategory.includes('insurance')) return 'insurance';

    // Default fallback - return the first part of the category
    return normalizedCategory.split('-')[0];
  };

  // Format date for display
  const formatDate = (dateString) => {
    if (!dateString) return '';

    try {
      // Check if it's already in the format we want (e.g., "15 Apr 25")
      if (/^\d{1,2}\s[A-Za-z]{3}\s\d{2}$/.test(dateString)) {
        return dateString;
      }

      // Otherwise, format the date
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return dateString; // If invalid date, return original

      const day = date.getDate();
      const month = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'][date.getMonth()];
      const year = date.getFullYear().toString().substr(-2);

      return `${day} ${month} ${year}`;
    } catch (e) {
      console.error('Error formatting date:', e);
      return dateString;
    }
  };

  const handleBookmark = async (jobId) => {
    if (!user) return;

    try {
      const isBookmarked = bookmarkedJobs.includes(jobId);

      if (isBookmarked) {
        const { error } = await supabase
          .from('my_applications')
          .delete()
          .match({ user_id: user.id, job_id: jobId });

        if (error) throw error;

        setBookmarkedJobs(prev => prev.filter(id => id !== jobId));
      } else {
        const { error } = await supabase
          .from('my_applications')
          .insert([
            {
              user_id: user.id,
              job_id: jobId,
              status: 'bookmarked',
              created_at: new Date().toISOString()
            }
          ]);

        if (error) throw error;

        setBookmarkedJobs(prev => [...prev, jobId]);
      }
    } catch (error) {
      console.error('Error toggling bookmark:', error);
    }
  };

  const handleShareClick = (job, event) => {
    event.stopPropagation();
    setShareDropdown({ open: true, job });
  };

  const handleCloseShare = () => {
    setShareDropdown({ open: false, job: null });
  };

  const handleBookmarkInternalJob = async (jobId) => {
    if (!user) return;

    try {
      const isBookmarked = bookmarkedInternalJobs.includes(jobId);

      if (isBookmarked) {
        const { error } = await supabase
          .from('applications')
          .delete()
          .eq('user_id', user.id)
          .eq('job_listing_id', jobId)
          .eq('status', 'bookmarked');

        if (error) throw error;

        setBookmarkedInternalJobs(prev => prev.filter(id => id !== jobId));
        showSuccess("Job removed from bookmarks");
      } else {
        const now = new Date().toISOString();
        const { error } = await supabase
          .from('applications')
          .upsert({
            user_id: user.id,
            job_listing_id: jobId,
            status: 'bookmarked',
            stage: 'Applications',
            application_date: now,
            last_updated: now
          }, {
            onConflict: 'user_id,job_listing_id'
          });

        if (error) throw error;

        setBookmarkedInternalJobs(prev => [...prev, jobId]);
        showSuccess("Job bookmarked");
      }
    } catch (error) {
      console.error('Error toggling bookmark:', error);
      showError("Failed to bookmark job. Please try again.");
    }
  };

  const handleRemoveJobMatch = async (jobId) => {
    const confirmed = window.confirm("Are you sure you want to remove this job match?");
    if (confirmed) {
      try {
        setFeaturedJobs(prev => prev.filter(job => job.id !== jobId));
        const updatedDismissed = [...dismissedJobMatches, jobId];
        setDismissedJobMatches(updatedDismissed);

        const { error } = await supabase.auth.updateUser({
          data: {
            dismissed_job_matches: updatedDismissed
          }
        });

        if (error) throw error;

        showSuccess("Job match removed");
      } catch (error) {
        setDismissedJobMatches(dismissedJobMatches);
        showError("Failed to remove job match. Please try again.");
      }
    }
  };

  if (loading) {
    return <div className="container">Loading...</div>;
  }

  if (error) {
    return <div className="container">{error}</div>;
  }

  return (
    <>
      <style>{`
      @media (max-width: 1024px) {
        .category-filters {
          display: none !important;
        }
        .category-filters-mobile {
          display: block !important;
          margin-bottom: 1rem !important;
        }
        .category-filters-mobile select {
          width: 100% !important;
          padding: 0.75rem !important;
          font-size: 0.875rem !important;
          border: 1px solid #e5e7eb !important;
          border-radius: 0.5rem !important;
          margin-bottom: 0.75rem !important;
        }
        .category-filters-mobile label {
          display: block !important;
          font-size: 0.875rem !important;
          font-weight: 500 !important;
          color: #374151 !important;
          margin-bottom: 0.5rem !important;
        }
        .category-filters-selected {
          display: flex !important;
          flex-wrap: wrap !important;
          gap: 0.5rem !important;
          margin-top: 0.75rem !important;
        }
        .category-filters-selected span {
          font-size: 0.75rem !important;
          padding: 0.375rem 0.75rem !important;
        }
      }
      @media (max-width: 768px) {
        .slogan-first-heading {
          font-size: 22px !important;
        }
      }
    `}</style>
      <div className="flex flex-col min-h-screen bg-white">
        {/* <Navbar /> */}
        <header className="header">
          <div className="header-content">
            {/* <div className="logo-container">
            <div className="logo">
              Doctors' Jobs <span className="logo-badge">Beta</span>
            </div>
          </div> */}

            <div className="slogan">
              <h1 className='font-medium slogan-first-heading'>Empowering Healthcare Careers.</h1>
              <h1 className='font-medium'>One opportunity at a time.</h1>
              <p className='text-[22px] mt-4'>Find better roles, optimise your CV, and stay organised, all in one powerful platform.</p>
            </div>

            <form onSubmit={handleSearch}>
              <div className={`search-container ${!user ? 'opacity-70 cursor-pointer' : ''}`} onClick={!user ? () => window.location.href = '/login' : undefined}>
                <div className="search-input">
                  <IoLocationOutline />
                  <select
                    value={locationFilter}
                    onChange={(e) => setLocationFilter(e.target.value)}
                    aria-label="Filter by location"
                    disabled={!user}
                  >
                    <option value="">Country/Region</option>
                    {availableLocations.map((location, index) => (
                      <option key={index} value={location}>{location}</option>
                    ))}
                  </select>
                </div>

                <div className="search-input">
                  <IoLocationOutline />
                  <select
                    value={provinceFilter}
                    onChange={(e) => setProvinceFilter(e.target.value)}
                    aria-label="Filter by province"
                    disabled={!user}
                  >
                    <option value="">Provinces</option>
                    {availableProvinces.map((province, index) => (
                      <option key={index} value={province}>{province}</option>
                    ))}
                  </select>
                </div>

                <div className="search-input">
                  <IoBagHandleOutline />
                  <select
                    value={categoryFilter}
                    onChange={(e) => setCategoryFilter(e.target.value)}
                    aria-label="Filter by category"
                    disabled={!user}
                  >
                    <option value="">Category</option>
                    {availableCategories.map((category, index) => (
                      <option key={index} value={category}>{category}</option>
                    ))}
                  </select>
                </div>

                <div className="search-input">
                  <IoStorefrontOutline />
                  <select
                    value={jobTypeFilter}
                    onChange={(e) => setJobTypeFilter(e.target.value)}
                    aria-label="Filter by job type"
                    disabled={!user}
                  >
                    <option value="">Job Types</option>
                    {availableJobTypes.map((type, index) => (
                      <option key={index} value={type}>{type}</option>
                    ))}
                  </select>
                </div>

                {availableTags.length > 0 && (
                  <div className="search-input">
                    <FaTag />
                    <select
                      value={tagFilter}
                      onChange={(e) => setTagFilter(e.target.value)}
                      aria-label="Filter by tag"
                      disabled={!user}
                    >
                      <option value="">All Tags</option>
                      {availableTags.map((tag, index) => (
                        <option key={index} value={tag}>{tag}</option>
                      ))}
                    </select>
                  </div>
                )}

                <button className="search-button" type="submit">
                  Search
                </button>
              </div>
              {!user && (
                <div className="mt-2 text-center text-sm text-blue-600">
                  <FaLock className="inline-block mr-1" />
                  <Link to="/login" className="font-medium hover:underline">
                    Log in to unlock full search and filtering capabilities
                  </Link>
                </div>
              )}
            </form>
          </div>

          <div className="paper-planes">
            <div
              className="paper-plane"
              style={{
                top: '15%',
                right: '15%',
                transform: 'rotate(45deg) scale(1)'
              }}
            >
              <FaPaperPlane size={68} color="#ddd" />
            </div>
            <div
              className="paper-plane"
              style={{
                top: '35%',
                right: '15%',
                transform: 'rotate(40deg) scale(1)'
              }}
            >
              <FaPaperPlane size={60} color="#ddd" />
            </div>
            <div
              className="paper-plane"
              style={{
                top: '25%',
                right: '29%',
                transform: 'rotate(35deg) scale(1)'
              }}
            >
              <FaPaperPlane size={52} color="#ddd" />
            </div>
          </div>
        </header>

        <main className="main-content">
          <div className="content-container">
            {user && featuredJobs.length > 0 && (
              <FeaturedJobs
                featuredJobs={featuredJobs}
                bookmarkedInternalJobs={bookmarkedInternalJobs}
                shareDropdown={shareDropdown}
                handleShareClick={handleShareClick}
                handleCloseShare={handleCloseShare}
                handleBookmarkInternalJob={handleBookmarkInternalJob}
                handleRemoveJobMatch={handleRemoveJobMatch}
              />
            )}
            {availableCategories.length > 0 && (
              <>
                <h2 className="section-title">Browse by category</h2>

                {/* Desktop Category Pills */}
                <div
                  className={`hidden md:flex category-filters ${!user ? 'opacity-70 cursor-pointer' : ''}`}
                  onClick={!user ? () => window.location.href = '/login' : undefined}
                >
                  {availableCategories.map((category, index) => (
                    <button
                      key={index}
                      className={`category-pill ${getCategoryClass(category)} ${categoryFilter === category ? 'active' : ''}`}
                      onClick={() => handleCategoryClick(category)}
                      disabled={!user}
                    >
                      {category}
                    </button>
                  ))}
                  {user && (categoryFilter || locationFilter || provinceFilter || jobTypeFilter || tagFilter || searchQuery || sortOrder !== 'default') && (
                    <button
                      className="category-pill clear-all"
                      onClick={clearAllFilters}
                    >
                      Clear All
                    </button>
                  )}
                </div>

                {/* Mobile Category Dropdown */}
                <div className="md:hidden category-filters-mobile">
                  <label>Browse by category</label>
                  <select
                    value={categoryFilter}
                    onChange={(e) => {
                      const val = e.target.value;
                      if (val === '') {
                        clearAllFilters();
                      } else {
                        handleCategoryClick(val);
                      }
                    }}
                    disabled={!user}
                    className="w-full bg-white border border-gray-200 rounded-lg py-2 px-3 text-sm"
                  >
                    <option value="">All Categories</option>
                    {availableCategories.map((category, index) => (
                      <option key={index} value={category}>{category}</option>
                    ))}
                  </select>
                  {categoryFilter && (
                    <div className="category-filters-selected">
                      <span className={`category-pill ${getCategoryClass(categoryFilter)} active`}>
                        {categoryFilter}
                      </span>
                      {user && (
                        <button
                          className="category-pill clear-all"
                          onClick={clearAllFilters}
                        >
                          Clear All
                        </button>
                      )}
                    </div>
                  )}
                  {!user && (
                    <div className="mt-2 text-center text-sm text-blue-600">
                      <FaLock className="inline-block mr-1" />
                      <Link to="/login" className="font-medium hover:underline">
                        Sign in to use category filters
                      </Link>
                    </div>
                  )}
                </div>
              </>
            )}

            {!user && (
              <div className="hidden md:block mt-2 mb-4 text-center text-sm text-blue-600">
                <FaLock className="inline-block mr-1" />
                <Link to="/login" className="font-medium hover:underline">
                  Sign in to use category filters
                </Link>
              </div>
            )}

            <div className="results-header">
              <div className="results-and-order">
                <h2 className="results-count">
                  Results ({!user ? `Showing 5 of ${filteredJobs.length}` : filteredJobs.length})
                </h2>
                {findMostRecentUpdate(jobs) && (
                  <div className="last-updated-info">
                    Last updated: {findMostRecentUpdate(jobs)}
                  </div>
                )}
              </div>
              <div className={`order-controls ${!user ? 'opacity-70 pointer-events-none' : ''}`}>
                <label htmlFor="sort-order">Order by: </label>
                <select
                  id="sort-order"
                  value={sortOrder}
                  onChange={(e) => setSortOrder(e.target.value)}
                  className="sort-selector"
                  disabled={!user}
                  style={{
                    background: 'transparent',
                    border: 'none',
                    color: '#333',
                    fontSize: '15px',
                    fontFamily: '"Helvetica Neue", Helvetica, Arial, sans-serif',
                  }}
                >
                  <option value="default">Default</option>
                  <option value="newest">Newest</option>
                  <option value="oldest">Oldest</option>
                  <option value="linkedin-yes">LinkedIn Posts</option>
                  <option value="linkedin-no">Non-LinkedIn Posts</option>
                </select>
              </div>
            </div>

            <table className="jobs-table">
              <thead>
                <tr>
                  <th style={{ display: !user && "none" }}>Pin</th>
                  <th>Company</th>
                  <th>Location</th>
                  <th>Title</th>
                  <th>Type</th>
                  <th>Experience</th>
                  <th>Category</th>
                  <th>Application</th>
                </tr>
              </thead>
              <tbody>
                {displayedJobs.map((job, index) => (
                  <tr
                    key={job.id}
                    className={`${!user && index === 5 ? "relative blur-none" : ""} ${bookmarkedJobs.includes(job.id) ? "bg-gray-50" : ""}`}
                  >
                    <td data-label="Pin" style={{ display: !user && "none" }}>
                      <button
                        onClick={() => handleBookmark(job.id)}
                        className="text-gray-400 hover:text-black transition-colors"
                      >
                        {bookmarkedJobs.includes(job.id) ? (
                          <IoBookmark className="text-black" />
                        ) : (
                          <IoBookmarkOutline />
                        )}
                      </button>
                    </td>
                    <td data-label="Company">{job.company}</td>
                    <td data-label="Location">{job.location}</td>
                    <td data-label="Title">{job.title}</td>
                    <td data-label="Type">{capitalizeFirstLetter(job.fulltime_parttime)}</td>
                    <td data-label="Experience">{capitalizeFirstLetter(job.experience)}</td>
                    <td data-label="Category">
                      <div className={`job-category-tag ${getCategoryClass(job.category)}`}>
                        {job.category}
                      </div>
                    </td>
                    <td data-label="Application">
                      {job.deadline && (
                        <div className="deadline">{formatDate(job.deadline)}</div>
                      )}
                      <a href={job.application_url} target="_blank" rel="noopener noreferrer">
                        <button className="apply-button">View Job</button>
                      </a>
                    </td>

                  </tr>
                ))}
              </tbody>
            </table>

            {/* Show a CTA button if not logged in and there are more than 6 jobs */}
            {!user && filteredJobs.length > 5 && (
              <div className="mt-8 text-center">
                <p className="text-lg text-gray-700 mb-4">
                  <span className="font-medium">{filteredJobs.length - 5}</span> more jobs available
                </p>
                <Link
                  to="/login"
                  className="inline-block bg-blue-600 hover:bg-blue-700 text-white py-3 px-6 rounded-full transition-all duration-200 shadow-md hover:shadow-lg font-medium"
                >
                  Sign in to see all {filteredJobs.length} jobs
                </Link>
              </div>
            )}

            <div className="disclaimer-text">
              *These jobs are posted on LinkedIn, which charges daily to advertise. We recommend applying quickly, as posts may open and close intermittently during the review process.
            </div>
          </div>
        </main>

        {!user && (
          <div className="mt-8">
            <JobApplicationsTracker />
            <InterviewPracticeSection />
            <CVTracker />
            <JobBoardSection />
            <FAQSection />
          </div>
        )}

        {/* <footer className="footer">
        <div className="footer-links">
          <a href="https://www.fourminutemedicine.com/files/terms-and-conditions.pdf" target="_blank" rel="noopener noreferrer">Disclaimer</a> | 
          <a href="https://www.fourminutemedicine.com/files/privacy-policy.pdf" target="_blank" rel="noopener noreferrer">Privacy Policy</a> | 
          <a href="https://www.fourminutemedicine.com/files/terms-of-service.pdf" target="_blank" rel="noopener noreferrer">Terms of Service</a> | 
          <a href="https://www.fourminutemedicine.com/contact-us" target="_blank" rel="noopener noreferrer">Contact Us</a>
        </div>
        <div className="footer-copyright">
          Copyright © 2025 Four Minute Medicine (PTY) LTD. All rights reserved.
        </div>
      </footer> */}
      </div>
    </>
  );
};

export default DoctorsJobsPage;